import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Group } from "@mantine/core";
import { AdminApp } from "./admin/AdminApp";
import { ArtworkPins } from "./admin/ArtworkPins";
import { Inspector } from "./admin/Inspector";
import { Queue } from "./admin/Queue";
import { ChatPanel } from "./chat/ChatPanel";
import { StreamList } from "./list/StreamList";
import { Player } from "./player/Player";
import { NewsPage } from "./news/NewsPage";
import { useListenLink } from "./player/useListenLink";
import { usePath } from "./router";
import { Timeline } from "./timeline/Timeline";
import { Chip } from "./ui/Chip";
import { Header } from "./ui/Header";
import { Shell } from "./ui/Shell";
import { useLayout } from "./ui/useLayout";
import { useResumeSync } from "./state/resume";
import { useListenedSync } from "./state/listened";
import { useMe, usePublicSettings } from "./api/queries";
import { atLeast } from "./api/types";
import { useAndroidPopupSync } from "./platform/androidPopup";

export function App() {
  const path = usePath();
  const settings = usePublicSettings();
  const { data: me, isLoading: meLoading } = useMe();
  const { t } = useTranslation();
  // Above the early returns below, because `usePath` re-renders this component in place: a
  // hook declared inside the listener branch would change the hook count on the first
  // navigation to /admin. Inert anywhere but a phone, where the two panels share the space.
  const [panel, setPanel] = useState<"streams" | "chat">("streams");
  // Above the early returns for the same reason. Five `matchMedia` listeners, read
  // synchronously on the first render, and the only ones in the app.
  const layout = useLayout();
  // Above the /admin branch below: the admin UI has the same player, and a position saved
  // there is the same listener's position.
  useResumeSync();
  useListenedSync();
  // /listen/155: the show's own address, which is what a Telegram or VK post links to.
  useAndroidPopupSync(me);
  useListenLink(path);

  // Everything under /admin is the editing UI. The API serves the same bundle for any path it
  // does not claim, so /admin is an address and not a mode held in memory.
  if (path.startsWith("/admin")) return <AdminApp />;

  // Covers is a page of its own, not a mode: it edits pins and the default cover and has
  // nothing to do with the timeline under the archive. A visitor without the role falls
  // through to the archive page below, which is the trade /review already makes for a
  // non-editor. It stays open while `me` resolves so an admin reloading it does not watch the
  // public page mount first.
  if (path === "/covers" && (meLoading || (!!me && atLeast(me.role, "admin"))))
    return (
      <Shell>
        <Shell.Header>
          <Header />
        </Shell.Header>
        <Shell.Main>
          <ArtworkPins />
        </Shell.Main>
      </Shell>
    );
  // News is a public section of its own: feed and detail pages with web/android filters.
  // It renders here rather than falling through to the Archive branch, so a visitor who
  // reaches /news sees News's own unavailable notice rather than the archive behind it.
  // NewsPage itself renders that state when news_enabled is false; the development gate
  // decides whether this branch is reached at all.
  if (path === "/news" || path.startsWith("/news/")) {
    return (
      <Shell>
        <Shell.Header>
          <Header />
        </Shell.Header>
        <Shell.Main>
          <NewsPage />
        </Shell.Main>
      </Shell>
    );
  }

  // Review is a mode of this page rather than a page of its own: the header, the player and
  // the canvas stay mounted across the switch, the queue takes the list's column, and the
  // inspector sits beside it with the player moved out to the edge (CSS, not markup order).
  // Held on while `me` is still resolving, so a reviewer reloading /review does not watch the
  // public list mount and fetch before being replaced.
  const review = path === "/review" && (meLoading || (!!me && atLeast(me.role, "editor")));
  const chatEnabled = (settings.data?.chat_enabled ?? false) && !review;

  // Archive with chat switched off has nothing for the third column, and an empty `Grid.Col`
  // still spends its span -- which is the gap that used to sit right of the player.
  const lowPanel = review || chatEnabled;
  // One column stacked vertically, versus list+player on one line with chat wrapped below.
  const stack = layout.phone && !layout.landscape;
  const stackedReview = stack && review;
  // Nothing to wrap below when there is no third panel: the two bands would then leave the
  // lower 40% of the page empty.
  const wrapped = lowPanel && !layout.phone && layout.twoColumn;
  // The two lines of the wrapped mode. `rowGap={0}` below is what lets these be plain
  // percentages that sum to 100 -- the separation between them is padding inside the lower
  // band instead, so nothing has to subtract a gap from a percentage.
  const topBand = wrapped ? (review ? "50%" : "60%") : undefined;
  const lowBand = wrapped ? (review ? "50%" : "40%") : undefined;

  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main style={{ padding: layout.phone ? "10px 12px" : 20 }}>
        <Grid
          columns={100}
          gap={layout.phone ? 10 : 16}
          // In the wrapped mode the two bands are exact percentages of the inner's height, so
          // the row gap has to be zero or they overflow by 16px; the lower band pays for the
          // separation with `pt` instead. In every other mode there is one line, and on a
          // phone the row gap IS the stack's spacing, so it must stay.
          rowGap={wrapped ? 0 : undefined}
          styles={{
            root: { height: stackedReview ? "auto" : "100%", minHeight: 0 },
            inner: {
              height: stackedReview ? "auto" : "100%",
              minHeight: 0,
              flexDirection: stack ? "column" : "row",
              // Only the wrapped mode has two lines. Everywhere else a stray wrap would
              // put panels side by side on a phone, because `Grid.Col` sets `flex-shrink: 0`.
              flexWrap: wrapped ? "wrap" : "nowrap",
            },
            col: { display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 },
          }}
        >
          {chatEnabled && (
            <Grid.Col
              span="content"
              display={layout.phone ? undefined : "none"}
              align={layout.landscape ? "flex-start" : undefined}
            >
              {/* A fieldset because the two chips are one control between them, which is
                  what the element says on its own. The UA's border, padding and default
                  minimum width come off, so the row draws exactly as it did. */}
              <Group
                component="fieldset"
                aria-label={t("app.panelLabel")}
                gap={6}
                style={{
                  border: 0,
                  margin: 0,
                  padding: 0,
                  minInlineSize: 0,
                  ...(layout.landscape && { flexDirection: "column" }),
                }}
              >
                <Chip pressed={panel === "streams"} onClick={() => setPanel("streams")}>
                  {t("app.panelStreams")}
                </Chip>
                <Chip pressed={panel === "chat"} onClick={() => setPanel("chat")}>
                  {t("app.panelChat")}
                </Chip>
              </Group>
            </Grid.Col>
          )}
          {/* Hidden with `display: none` rather than unmounted: that is what preserves the
              list's scroll position and its query cache across a phone tab switch. */}
          <Grid.Col
            span="auto"
            h={stackedReview ? "min(30dvh, 260px)" : topBand}
            style={stackedReview ? { flex: "0 0 auto" } : undefined}
            display={layout.phone && !review && panel !== "streams" ? "none" : undefined}
          >
            {review ? <Queue /> : <StreamList />}
          </Grid.Col>
          <Grid.Col
            span={stack ? "content" : layout.landscape ? 35 : layout.twoColumn ? 40 : 25}
            h={topBand}
            order={layout.phone ? 1 : !wrapped && review ? 3 : undefined}
          >
            <Player review={review} />
          </Grid.Col>
          {lowPanel && (
            <Grid.Col
              span={stack ? (review ? "content" : "auto") : wrapped ? 100 : review ? 32 : 20}
              h={lowBand}
              pt={wrapped ? 16 : undefined}
              order={!wrapped && review && !layout.phone ? 2 : undefined}
              display={layout.phone && !review && panel !== "chat" ? "none" : undefined}
            >
              {review ? <Inspector /> : <ChatPanel />}
            </Grid.Col>
          )}
        </Grid>
      </Shell.Main>
      <Shell.Footer>
        <Timeline editable={review} />
      </Shell.Footer>
    </Shell>
  );
}
